import { Component, OnInit, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { LogbookOverviewService } from '../../../core/api-services/logbook-overview.service';
import { DashboardService } from '../../../core/api-services/dashboard.service';

/**
 * Logbook Overview Component
 *
 * Landing page for the Logbook section showing stats, safety testing info,
 * report status, and quick navigation links.
 *
 * Reference: /agent-os/specs/2026-01-27-logbook-overview-page/spec.md
 */
@Component({
  selector: 'app-logbook-overview',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './logbook-overview.component.html',
  styleUrls: ['./logbook-overview.component.scss']
})
export class LogbookOverviewComponent implements OnInit {
  private logbookService = inject(LogbookOverviewService);
  private dashboardService = inject(DashboardService);

  isLoading = signal(true);

  // Logbook stats
  readonly totalAcresSprayed = computed(() => this.logbookService.stats$()?.totalAcresSprayed ?? 0);

  // Dashboard stats
  readonly upcomingFlights = computed(() => this.dashboardService.stats$()?.upcomingFlights ?? 0);
  readonly flightHours = computed(() => this.dashboardService.stats$()?.flightHours ?? 0);

  // Report dates
  readonly faaLastGenerated = computed(() => this.logbookService.reportDates$()?.faaLastGenerated ?? null);
  readonly stateLastGenerated = computed(() => this.logbookService.reportDates$()?.stateLastGenerated ?? null);

  // Loading states
  readonly isLoadingStats = computed(() => this.logbookService.isLoadingStats$() || this.dashboardService.isLoadingStats$());
  readonly isLoadingReportDates = computed(() => this.logbookService.isLoadingReportDates$());

  // Quick navigation links
  readonly quickLinks = [
    { label: 'Spray Records', icon: 'bi bi-journal-text', description: 'View and manage spray records', route: '/logbook/spray-records' },
    { label: 'Fields', icon: 'bi bi-geo-alt', description: 'Manage your fields and locations', route: '/logbook/fields' },
    { label: 'Chemical Book', icon: 'bi bi-book', description: 'Browse the chemical reference library', route: '/logbook/chemicals/book' },
    { label: 'Custom Chemicals', icon: 'bi bi-flask', description: 'Manage your custom chemical entries', route: '/logbook/chemicals/custom' },
    { label: 'Safety Tests', icon: 'bi bi-shield-check', description: 'View and take safety tests', route: '/logbook/testing/safety' },
    { label: 'Field Tests', icon: 'bi bi-clipboard-check', description: 'View and manage field tests', route: '/logbook/testing/field' },
    { label: 'FAA Reports', icon: 'bi bi-file-earmark-text', description: 'Generate and view FAA reports', route: '/logbook/reports/faa' },
    { label: 'State Reports', icon: 'bi bi-file-earmark-ruled', description: 'Generate and view state reports', route: '/logbook/reports/state' }
  ];

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    try {
      await Promise.all([
        this.logbookService.loadStats(),
        this.logbookService.loadReportDates(),
        this.dashboardService.loadStats()
      ]);
    } catch (error) {
      console.error('Error initializing logbook overview:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Format a date string as "Mon DD, YYYY"
   */
  formatDate(dateString: string | null): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}
